import { useMemo } from "react";
import { usePartners, usePrices } from "@stockline/web";
import type { PriceLookupMaps } from "@/query/hooks/useGlobalPrices";

export interface UseRecapPricesOfflineOptions {
  userId?: string;
  enabled?: boolean;
}

export interface UseRecapPricesOfflineResult {
  priceMaps: PriceLookupMaps;
  isLoading: boolean;
  isError: boolean;
  error: null;
}

/**
 * Recap-only adapter that sources pricing maps from the offline-first
 * `usePrices()` hook (Dexie + SSE) instead of polling the legacy bulk
 * partner / group price endpoints.
 *
 * Emits the same `PriceLookupMaps` shape `useGlobalPrices` exposed so the
 * existing `useEffectivePriceLookup` resolver (consumed by recap cells
 * like `ProductCell`) works unchanged.
 *
 * Behavior note: the new `Price` Dexie store is keyed on
 * `clientGroupId` only — partner-row-level prices were dropped from the
 * domain model. The two `partnerPrices` / `userPartnerPrices` slots of
 * `PriceLookupMaps` are therefore always empty; the resolver falls
 * through to client-group prices via `partnerGroups`, which is the
 * intended new behavior.
 *
 * `productPrices` is populated by `useRecapData` from the product feed
 * (we leave it empty here — same contract as the legacy hook).
 */
export function useRecapPricesOffline(
  options?: UseRecapPricesOfflineOptions,
): UseRecapPricesOfflineResult {
  const enabled = options?.enabled ?? true;
  const userId = options?.userId;

  const allPrices = usePrices();
  const partners = usePartners();

  const priceMaps = useMemo<PriceLookupMaps>(() => {
    const partnerPrices = new Map<string, Map<string, number>>();
    const groupPrices = new Map<string, Map<string, number>>();
    const productPrices = new Map<string, number>();
    const partnerGroups = new Map<string, string>();
    const userPartnerPrices = new Map<string, Map<string, number>>();
    const userGroupPrices = new Map<string, Map<string, number>>();

    if (!enabled) {
      return {
        partnerPrices,
        groupPrices,
        productPrices,
        partnerGroups,
        userPartnerPrices,
        userGroupPrices,
      };
    }

    // Partition price rows: company-level (userId === null) vs user-level
    // override (userId === <salesperson>). Apply the userId filter in JS
    // — Dexie returns every row in the store regardless.
    for (const row of allPrices) {
      if (row.userId === null) {
        const target = groupPrices.get(row.clientGroupId) ?? new Map();
        target.set(row.productId, row.price);
        groupPrices.set(row.clientGroupId, target);
      } else if (userId && row.userId === userId) {
        const target = userGroupPrices.get(row.clientGroupId) ?? new Map();
        target.set(row.productId, row.price);
        userGroupPrices.set(row.clientGroupId, target);
      }
    }

    // Partner → primary client group. A partner can belong to several groups
    // (e.g. VNA price list + CFC8 customer category + CUCL classification) and
    // their order in `clientGroupIds` is undefined (no ORDER BY in
    // partner_client_groups SELECT). Only one of those groups typically has
    // priced products — pick the first id that appears in groupPrices or
    // userGroupPrices, otherwise fall back to index 0.
    for (const partner of partners) {
      const ids = partner.clientGroupIds;
      if (!ids?.length) continue;
      const priced =
        ids.find((id) => groupPrices.has(id) || userGroupPrices.has(id)) ??
        ids[0];
      partnerGroups.set(partner.id, priced);
    }

    return {
      partnerPrices,
      groupPrices,
      productPrices,
      partnerGroups,
      userPartnerPrices,
      userGroupPrices,
    };
  }, [allPrices, partners, userId, enabled]);

  return {
    priceMaps,
    isLoading: false,
    isError: false,
    error: null,
  };
}
